import React, { useEffect, useState } from "react";
import { DataGridPro as DataGrid } from "@mui/x-data-grid-pro";
import Box from "@mui/material/Box";
import httpClient from "../utils/httpClient.js";
import { useDispatch, useSelector } from "react-redux";
import { setNotification } from "../redux/slices/notificationSlice.js";
import { formatDate, formatBooleanColumns, formatFault, formatVehicleBrand, formatVehicleModel, renderStatus } from '../utils/utils';
import { Tooltip, Chip, Card, Typography, TableRow, Table, TableCell, TableContainer, Paper, TableBody } from "@mui/material";
import AuditGrid from "../components/grids/auditGrid.js";
//import { parseTasks } from "../utils/utils.js";
//import { Button } from "@mui/material";

const CompletedTasksPage = () => {

  const [tasks, setTasks] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const dispatch = useDispatch();
  const constants = useSelector(state => state.constants.data);

  useEffect(() => {

    const getCompletedTasks = async () => {

      const go = true;
      let batchId = 1;
      const numberOfRecords = 5000;
      let data = [];

      while (go) {

        const res = await httpClient.get(`/tasks?statusCode=COMP&batchId=${batchId}&numberOfRecords=${numberOfRecords}`);
        if (res.status == 200) {

          const d = res.data;
          data.push(...d);

          if (d.length === 0) {
            break;
          }

          if (d.length < numberOfRecords) {
            setTasks(data);
            break;
          }
          batchId++;

        } else {
          dispatch(
            setNotification({ severity: 'error', message: 'Could not retrieve completed tasks.' })
          );
        }
      }
    }
    getCompletedTasks();

  }, []);

  const handleRowClick = (params) => {
    setSelectedEntity(params.row);
  };

  const renderType = (typeId) => {
    const type = constants.taskTypes.find(t => t.id == typeId);
    return <Tooltip title={type.name} arrow>
      <Chip label={type?.name} size="small" />
    </Tooltip>
  }


  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      description: 'Task ID',
      flex: 0.1,
      minWidth: 50,
    },
    {
      field: 'typeId',
      headerName: 'Type',
      description: 'Type',
      flex: 0.8,
      minWidth: 50,
      renderCell: (params) => renderType(params.row.typeId),
      valueGetter: (value) => {
        const type = constants.taskTypes.find(t => t.id === value);
        return type?.name || '';
      },
    },
    {
      field: 'customerName',
      headerName: 'Customer Name',
      description: 'Customer Name',
      flex: 0.5,
      minWidth: 150,
    },
    {
      field: 'vehicleMakeId',
      headerName: 'Vehicle Brand',
      description: 'Vehicle Brand',
      headeralign: 'left',
      align: 'left',
      flex: 1,
      maxWidth: 120,
      valueGetter: value => formatVehicleBrand(value, constants.vehicleMakes)
    },
    {
      field: 'vehicleModelId',
      headerName: 'Vehicle Model',
      description: 'Vehicle Model',
      headeralign: 'left',
      align: 'left',
      flex: 1,
      valueGetter: value => formatVehicleModel(value, constants.vehicleModels)
      //minWidth: 80,
    },
    {
      field: 'vehicleRegistrationNumber',
      headerName: 'Reg #',
      description: 'Vehicle Registration Number',
      headeralign: 'left',
      align: 'left',
      flex: 1,
      //minWidth: 80,
    },
    {
      field: 'isPriority',
      headerName: '⚠️',
      description: 'Is Priority',
      headeralign: 'left',
      align: 'left',
      flex: 0.5,
      maxWidth: 80,
      valueGetter: value => formatBooleanColumns(value),
      renderHeader: (params) => {
        return (
          <Tooltip title="Priority" arrow>
            <span style={{ padding: '0 8px' }}>{params.colDef.headerName}</span>
          </Tooltip>
        )
      }
    },
    {
      field: 'isHomestart',
      headerName: '🏠',
      description: 'Homestart',
      headeralign: 'left',
      align: 'left',
      flex: 0.5,
      maxWidth: 80,
      valueGetter: value => formatBooleanColumns(value),
      renderHeader: (params) => {
        return (
          <Tooltip title="Home Start" arrow>
            <span style={{ padding: '0 8px' }}>{params.colDef.headerName}</span>
          </Tooltip>
        )
      }
    },
    {
      field: 'requiresIntervention',
      headerName: '🧑‍🔧',
      description: 'Requires Intervention',
      headeralign: 'left',
      align: 'left',
      flex: 0.5,
      maxWidth: 80,
      valueGetter: value => formatBooleanColumns(value),
      renderHeader: (params) => {
        return (
          <Tooltip title="Requires Intervention" arrow>
            <span style={{ padding: '0 8px' }}>{params.colDef.headerName}</span>
          </Tooltip>
        )
      }
    },
    {
      field: 'locationDescription',
      headerName: 'Address',
      description: 'Address',
      headeralign: 'left',
      align: 'left',
      flex: 1,
      //minWidth:200,
    },
    {
      field: 'createdOn',
      headerName: 'Created',
      description: 'Created',
      headeralign: 'left',
      align: 'left',
      flex: 1,
      //valueGetter: (value) => formatDate(value),
      renderCell: (params) => {
        const formatted = formatDate(params.value);
        return (
          <Tooltip title={formatted}>
            {formatted}
          </Tooltip>
        )
      }
    },
    {
      field: 'statusId',
      headerName: 'Status',
      description: 'Status',
      headeralign: 'left',
      align: 'left',
      flex: 1,
      maxWidth: 90,
      renderCell: (params) => renderStatus(params.row.statusId, constants.taskStatuses),
      valueGetter: (value) => {
        const status = constants.taskStatuses.find(s => s.id === value);
        return status?.code || '';
      }
    },
    {
      field: 'faultId',
      headerName: 'Fault',
      description: 'Fault',
      headeralign: 'left',
      align: 'left',
      flex: 1,
      maxWidth: 120,
      valueGetter: value => formatFault(value, constants.vehicleFaults)
    },
    {
      field: 'caseId',
      headerName: 'CASE ID',
      description: 'CASE ID',
      headeralign: 'left',
      align: 'left',
      flex: 1,
    },
  ];
  return (
    <Box sx={{
      display: 'flex',
      width: "100%",
      flexDirection: { xs: 'column', ['@media (min-width:2400px)']: { flexDirection: 'row' } },
      gap: 2
    }}>
      <Box sx={{ flex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
          }}
        >
        </Box>
        {<DataGrid
          sx={{ width: 1 }}
          rows={tasks}
          columns={columns}
          onRowClick={handleRowClick}
          initialState={{
            pagination: { paginationModel: { pageSize: 20 } },
          }}
          pageSizeOptions={[10, 20, 50]}
          disableColumnResize
          density="compact"
          slotProps={{
            filterPanel: {
              filterFormProps: {
                logicOperatorInputProps: {
                  variant: "outlined",
                  size: "small",
                },
                columnInputProps: {
                  variant: "outlined",
                  size: "small",
                  sx: { mt: "auto" },
                },
                operatorInputProps: {
                  variant: "outlined",
                  size: "small",
                  sx: { mt: "auto" },
                },
                valueInputProps: {
                  InputComponentProps: {
                    variant: "outlined",
                    size: "small",
                  },
                },
              },
            },
          }}
        />}
      </Box>

      {selectedEntity &&
        <Card>
          <Typography variant="h4">Task Details</Typography>
          <TableContainer component={Paper}>
            <Table sx={{ maxWidth: 350 }} aria-label="simple table">
              <TableBody>

                <TableRow
                  key="details-1"
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    ID
                  </TableCell>
                  <TableCell align="right">{selectedEntity.id}</TableCell>
                </TableRow>

                <TableRow
                  key="details-2"
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    Case ID
                  </TableCell>
                  <TableCell align="right">{selectedEntity.caseId}</TableCell>
                </TableRow>

                <TableRow
                  key="details-3"
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    Created On
                  </TableCell>
                  <TableCell align="right">{formatDate(selectedEntity.createdOn, true)}</TableCell>
                </TableRow>

                <TableRow
                  key="details-9"
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    Completed On
                  </TableCell>
                  <TableCell align="right">{formatDate(selectedEntity.completionTime, true)}</TableCell>
                </TableRow>

                <TableRow
                  key="details-4"
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    Customer Name
                  </TableCell>
                  <TableCell align="right">{selectedEntity.customerName}</TableCell>
                </TableRow>

                <TableRow
                  key="details-5"
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    Customer Phone
                  </TableCell>
                  <TableCell align="right">{selectedEntity.contactPhoneNumber}</TableCell>
                </TableRow>

                <TableRow
                  key="details-6"
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    Customer Address
                  </TableCell>
                  <TableCell align="right">{selectedEntity.customerAddress}</TableCell>
                </TableRow>

                <TableRow
                  key="details-7"
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    Customer PO Code
                  </TableCell>
                  <TableCell align="right">{selectedEntity.customerPostcode}</TableCell>
                </TableRow>

                <TableRow
                  key="details-6"
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    Breakdown Location
                  </TableCell>
                  <TableCell align="right">{selectedEntity.locationDescription}</TableCell>
                </TableRow>
                {selectedEntity.typeId == 2 && 
                  <TableRow
                    key="details-6"
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      Recovery Location
                    </TableCell>
                    <TableCell align="right">{selectedEntity.recoveryDestinationText}</TableCell>
                  </TableRow>
                }
                <TableRow
                  key="details-8"
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    Completion Reason
                  </TableCell>
                  <TableCell align="right">{constants.completionCodes.find(c => c.code == selectedEntity.completionCode)?.name}</TableCell>
                </TableRow>

                <TableRow
                  key="details-10"
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    Completion Parts
                  </TableCell>
                  <TableCell align="right">{selectedEntity.completionPartsSummary}</TableCell>
                </TableRow>

                <TableRow
                  key="details-11"
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    Completion Notes
                  </TableCell>
                  <TableCell align="right">{selectedEntity.completionNotes}</TableCell>
                </TableRow>

              </TableBody>
            </Table>
          </TableContainer>
        </Card>}

        <Card>
          <Typography variant="h4">Task Audit</Typography>
          {selectedEntity && <AuditGrid entityId={selectedEntity.id} />}
        </Card>
    </Box>

  );
};

export default CompletedTasksPage;
